import translate from '@translate';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import FormBlock from 'src/components/FormBlock';
import { setLocationParams } from 'src/resources/location/reducer';
import { RootStoreType } from 'src/types/store';
import { ProviderCompanyType } from 'src/types/providerCompany';
import { checkFormValidity, getEditData } from 'src/utils/formFunctions';
import Loading from 'src/components/Loading/intex';
import { useCheckPermission } from 'src/utils/checkPermission';
import Detail from './Detail';
import TopBlock from './TopBlock';
import {
    clearProviderCompanyDetail,
    createProviderCompanyDetailFetch,
    deleteProviderCompanyFetch,
    getProviderCompanyDetailFetch,
    updateProviderCompanyDetailFetch
} from 'src/resources/providerCompanyDetail/reducer';
import { addMessage } from 'src/resources/alertMessages/reducer';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import { FormErrorsType, OnChangeType } from '../../types/global';

export type EditKeysType =
    | 'RECIPIENT_NAME'
    | 'CONTACT_PERSON'
    | 'COMPANY_NAME'
    | 'GLN'
    | 'EMAIL'
    | 'PHONE'
    | 'COMPANY_ID'
    | 'STREET'
    | 'CITY'
    | 'ZIP_CODE'
    | 'STATE'
    | 'ADDRESS_ADDITIONAL_INFO'
    | 'PROVINCE_CODE';

export const EDIT_KEYS = [
    'RECIPIENT_NAME',
    'CONTACT_PERSON',
    'COMPANY_NAME',
    'GLN',
    'EMAIL',
    'PHONE',
    'COMPANY_ID',
    'STREET',
    'CITY',
    'ZIP_CODE',
    'STATE',
    'ADDRESS_ADDITIONAL_INFO',
    'PROVINCE_CODE'
];

const initData = {};
const ProviderCompanyDetailContainer: FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { idProviderCompany } = useParams();

    const newProviderCompany = !idProviderCompany;

    const formRef = useRef<HTMLFormElement>(null);

    const data = useSelector((state: RootStoreType) => state.providerCompanyDetail.data);
    const loading = useSelector((state: RootStoreType) => state.providerCompanyDetail.loading);
    const deleted = useSelector((state: RootStoreType) => state.providerCompanyDetail.deleted);

    const [editData, setEditData] = useState<ProviderCompanyType>(getEditData(initData || {}, EDIT_KEYS));
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});
    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);

    const useCheckPermissionCreate = useCheckPermission('PROVIDER_COMPANIES.CREATE');
    const useCheckPermissionUpdate = useCheckPermission('PROVIDER_COMPANIES.UPDATE');
    const disableEdit = newProviderCompany ? !useCheckPermissionCreate : !useCheckPermissionUpdate;
    const useCheckPermissionDelete = useCheckPermission('PROVIDER_COMPANIES.DELETE');
    const disableDelete = newProviderCompany || !useCheckPermissionDelete;

    const backUrl = '/settings/provider_companies/';

    useEffect(() => {
        if (idProviderCompany) {
            dispatch(
                setLocationParams({ name: translate('providerCompanyDetail.title', { idProviderCompany }), backUrl })
            );
        } else {
            dispatch(setLocationParams({ name: translate('providerCompanyDetail.titleNew'), backUrl }));
        }
    }, [idProviderCompany]);

    useEffect(() => {
        if (idProviderCompany) {
            dispatch(getProviderCompanyDetailFetch(idProviderCompany));
        } else {
            setEditData(initData);
        }
        return () => {
            dispatch(clearProviderCompanyDetail());
        };
    }, [idProviderCompany]);

    useEffect(() => {
        if (data) {
            setEditData(data);
        }

        if (!idProviderCompany && data?.ID) {
            navigate(`/settings/provider_companies/${String(data.ID)}/`);
        }
    }, [data]);

    useEffect(() => {
        if (deleted) {
            navigate(backUrl);
        }
    }, [deleted]);

    const onChange: OnChangeType<EditKeysType> = (key, value, error) => {
        const newData = { ...editData };
        const newErrors = { ...editErrors };
        newData[key] = value;
        newErrors[key] = error;
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const handleDeleteProviderCompany = () => {
        setConfirmParams({
            title: translate('providerCompanyDetail.deleteProviderCompany'),
            content: translate('providerCompanyDetail.deleteProviderCompanyConfirm'),
            action: () => dispatch(deleteProviderCompanyFetch(idProviderCompany))
        });
    };

    const handleSave = () => {
        const newFormErrors = checkFormValidity(formRef);

        if (!Object.keys(newFormErrors).length) {
            setConfirmParams({
                title: translate(newProviderCompany ? 'providerCompanyDetail.titleNew' : 'providerCompanyDetail.title', { idProviderCompany }),
                content: translate(
                    newProviderCompany ? 'providerCompanyDetail.createConfirm' : 'providerCompanyDetail.saveConfirm'
                ),
                action: () => {
                    const formatData = { ...editData };

                    if (newProviderCompany) {
                        dispatch(createProviderCompanyDetailFetch(formatData));
                    } else {
                        dispatch(updateProviderCompanyDetailFetch({ idProviderCompany, data: formatData }));
                    }
                }
            });
        } else {
            dispatch(
                addMessage({
                    type: 'warning',
                    title: translate('messages.formWarningTitle'),
                    text: translate('messages.formWarningText')
                })
            );
        }

        setEditErrors(newFormErrors);
    };

    return (
        <>
            <TopBlock
                data={data}
                idProviderCompany={idProviderCompany}
                newProviderCompany={newProviderCompany}
                disableEdit={disableEdit}
                disableDelete={disableDelete}
                handleSave={handleSave}
                handleDeleteProviderCompany={handleDeleteProviderCompany}
            />
            <form ref={formRef}>
                {loading && <Loading />}

                <FormBlock>
                    <Detail
                        onChange={onChange}
                        editData={editData}
                        editErrors={editErrors}
                        newProviderCompany={newProviderCompany}
                        disableEdit={disableEdit}
                    />
                </FormBlock>
            </form>
            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    action={confirmParams.action}
                    onClose={(): any => setConfirmParams(null)}
                />
            )}
        </>
    );
};

export default ProviderCompanyDetailContainer;
